/**
 * Every schema.org builder the public site emits (ADR 012, plan item S4).
 *
 * One module so a type's required properties are decided once: pages, the
 * page kit (`components/seo/`), the blog and the root layout all call these
 * instead of writing object literals. `web/tests/unit/seoKitJsonLd.test.ts`
 * holds each builder to the properties search engines require for its type.
 *
 * Top-level builders carry `@context`; entities nested inside another node
 * (an author, a reviewer, a list item) do not.
 */
import {
  ORGANIZATION,
  getPage,
  getPerson,
  hasProfile,
  type Organization,
  type PageSpec,
} from '@roomtreatment/seo-content';
import { authorHref } from './authors';
import { SITE_NAME, SITE_URL, absoluteUrl } from './site';

type Node = Record<string, unknown>;

const CONTEXT = 'https://schema.org';

/** Stable id so every `author`/`publisher` reference resolves to one entity. */
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;

export interface BreadcrumbItem {
  name: string;
  path: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface HowToStep {
  name?: string;
  text: string;
}

function withContext(node: Node): Node {
  return { '@context': CONTEXT, ...node };
}

/** A reference to the site organization, for use inside another node. */
export function organizationRef(): Node {
  return { '@type': 'Organization', '@id': ORGANIZATION_ID, name: SITE_NAME };
}

/** The people.json id of the founder named in the Organization node. */
export const FOUNDER_ID = 'founder';

/**
 * The site organization, emitted once site-wide by the root layout. Company
 * facts come from contracts/seo/organization.json and the founder from
 * people.json; each property appears only once its data is filled in.
 */
export function organization(org: Organization = ORGANIZATION): Node {
  const founder = getPerson(FOUNDER_ID);
  return withContext({
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: SITE_NAME,
    url: SITE_URL,
    logo: absoluteUrl('/brand/icon-512.png'),
    description:
      'Free 3D room acoustics solver and treatment calculator using real physics — room modes, first reflections, and speaker placement.',
    ...(org.legalName ? { legalName: org.legalName } : {}),
    ...(org.foundingDate ? { foundingDate: org.foundingDate } : {}),
    ...(org.country ? { address: { '@type': 'PostalAddress', addressCountry: org.country } } : {}),
    ...(org.sameAs.length ? { sameAs: org.sameAs } : {}),
    ...(org.contactEmail
      ? { contactPoint: { '@type': 'ContactPoint', contactType: 'customer support', email: org.contactEmail } }
      : {}),
    ...(founder && hasProfile(founder) ? { founder: personNode(FOUNDER_ID) } : {}),
  });
}

/**
 * A person (or the editorial team) from contracts/seo/people.json, as a
 * nested node. The editorial team IS the site organization, so it resolves to
 * the organization's id rather than a second, unlinked entity.
 */
export function personNode(personId: string): Node {
  const p = getPerson(personId);
  if (!p) throw new Error(`jsonld: no person with id "${personId}" in contracts/seo/people.json`);
  // F12: this must resolve to EXACTLY organizationRef(), name included — the
  // doc comment above says the editorial team IS the organization rather
  // than a second, unlinked entity, but overriding `name` here used to leave
  // two nodes sharing ORGANIZATION_ID with conflicting `name`s (this one, and
  // the root layout's real Organization node), which is invalid for the same
  // reason two nodes can't share an @id with two different addresses.
  if (p.kind === 'organization') return organizationRef();
  const href = authorHref(personId);
  return {
    '@type': 'Person',
    name: p.name,
    ...(href ? { url: absoluteUrl(href) } : {}),
    ...(p.role ? { jobTitle: p.role } : {}),
    ...(p.bio ? { description: p.bio } : {}),
    ...(p.credentials.length
      ? { hasCredential: p.credentials.map((name) => ({ '@type': 'EducationalOccupationalCredential', name })) }
      : {}),
    ...(p.photo ? { image: absoluteUrl(p.photo) } : {}),
    ...(p.sameAs.length ? { sameAs: p.sameAs } : {}),
  };
}

export function person(personId: string): Node {
  return withContext(personNode(personId));
}

/** An `/authors/<id>` page: a ProfilePage whose main entity is the person. */
export function profilePage(personId: string, path: string): Node {
  return withContext({
    '@type': 'ProfilePage',
    url: absoluteUrl(path),
    mainEntity: personNode(personId),
  });
}

export interface WebPageOptions {
  /** ISO date; defaults to the registry's `updatedAt`. */
  dateModified?: string;
  /** Person id; defaults to the registry's `reviewerId`. `null` omits it. */
  reviewedBy?: string | null;
}

export function webPage(pageId: string, options: WebPageOptions = {}): Node {
  const spec = getPage(pageId);
  const reviewerId = options.reviewedBy === undefined ? spec.reviewerId : options.reviewedBy;
  return generatedWebPage({
    name: spec.h1,
    description: spec.description,
    path: spec.path,
    authorId: spec.authorId,
    dateModified: options.dateModified ?? spec.updatedAt,
    reviewerId,
    reviewedAt: spec.reviewedAt,
  });
}

export interface GeneratedWebPageInput {
  name: string;
  description: string;
  path: string;
  authorId: string;
  dateModified?: string;
  reviewerId?: string | null;
  reviewedAt?: string | null;
}

/** A WebPage for a generated page that has no registry entry (room sizes). */
export function generatedWebPage(input: GeneratedWebPageInput): Node {
  const { reviewerId, reviewedAt } = input;
  return withContext({
    '@type': 'WebPage',
    name: input.name,
    description: input.description,
    url: absoluteUrl(input.path),
    ...(input.dateModified ? { dateModified: input.dateModified } : {}),
    author: personNode(input.authorId),
    publisher: organizationRef(),
    ...(reviewerId ? { reviewedBy: personNode(reviewerId) } : {}),
    ...(reviewerId && reviewedAt ? { lastReviewed: reviewedAt } : {}),
  });
}

/** A free, in-browser calculator page. */
export function softwareApplication(pageId: string): Node {
  const spec: PageSpec = getPage(pageId);
  return withContext({
    '@type': 'SoftwareApplication',
    name: spec.h1,
    description: spec.description,
    url: absoluteUrl(spec.path),
    applicationCategory: 'MultimediaApplication',
    operatingSystem: 'All',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  });
}

export interface ProductInput {
  name: string;
  brand: string;
  model: string;
  description: string;
  path: string;
  /** Site path of a product image, when one exists. */
  image?: string | null;
  /** Manufacturer's list price and the month it was recorded (`YYYY-MM`). */
  msrp?: { usd: number; asOf: string } | null;
}

/**
 * A catalogue product (plan item S24), without `@context` — for nesting
 * inside another node (a gear-matching pair page's `mentions`, two products
 * under one WebPage — never a second top-level entity). The only price we
 * publish is the manufacturer's list price, so the offer says exactly that:
 * a ListPrice price specification with its date, never a live price, and no
 * rating, because we collect none.
 */
export function productNode(input: ProductInput): Node {
  const url = absoluteUrl(input.path);
  const price = input.msrp ? input.msrp.usd.toFixed(2) : null;
  return {
    '@type': 'Product',
    name: input.name,
    brand: { '@type': 'Brand', name: input.brand },
    model: input.model,
    description: input.description,
    url,
    ...(input.image ? { image: absoluteUrl(input.image) } : {}),
    ...(input.msrp && price
      ? {
          offers: {
            '@type': 'Offer',
            url,
            price,
            priceCurrency: 'USD',
            description: `Manufacturer's suggested retail price (MSRP) as of ${input.msrp.asOf}, not a current store price.`,
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price,
              priceCurrency: 'USD',
              priceType: 'https://schema.org/ListPrice',
            },
          },
        }
      : {}),
  };
}

/** A catalogue product page (plan item S24): `productNode` at the top level, with `@context`. */
export function product(input: ProductInput): Node {
  return withContext(productNode(input));
}

export function faqPage(items: readonly FaqItem[]): Node {
  return withContext({
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  });
}

export function howTo(name: string, steps: readonly HowToStep[]): Node {
  return withContext({
    '@type': 'HowTo',
    name,
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      ...(s.name ? { name: s.name } : {}),
      text: s.text,
    })),
  });
}

export function definedTerm(term: { name: string; description: string }, url: string): Node {
  return withContext({
    '@type': 'DefinedTerm',
    name: term.name,
    description: term.description,
    url: absoluteUrl(url),
    inDefinedTermSet: absoluteUrl('/glossary'),
  });
}

/** The glossary index: every term, each pointing at its own page. */
export function definedTermSet(
  pageId: string,
  terms: readonly { name: string; description: string; path: string }[],
): Node {
  const spec = getPage(pageId);
  return withContext({
    '@type': 'DefinedTermSet',
    name: spec.h1,
    url: absoluteUrl(spec.path),
    hasDefinedTerm: terms.map((t) => ({
      '@type': 'DefinedTerm',
      name: t.name,
      description: t.description,
      url: absoluteUrl(t.path),
    })),
  });
}

export interface ArticleOptions {
  /** ISO date the article first went out. */
  datePublished?: string;
  /** schema.org subtype, e.g. `TechArticle`. */
  type?: 'Article' | 'TechArticle' | 'BlogPosting';
}

export function article(pageId: string, options: ArticleOptions = {}): Node {
  const spec = getPage(pageId);
  return withContext({
    '@type': options.type ?? 'Article',
    headline: spec.h1,
    description: spec.description,
    url: absoluteUrl(spec.path),
    ...(options.datePublished ? { datePublished: options.datePublished } : {}),
    dateModified: spec.updatedAt,
    author: personNode(spec.authorId),
    publisher: organizationRef(),
  });
}

/** A hub page listing other pages (the /tools, /guides and /blog indexes). */
export function collectionPage(pageId: string, items: readonly BreadcrumbItem[]): Node {
  const spec = getPage(pageId);
  return withContext({
    '@type': 'CollectionPage',
    name: spec.h1,
    description: spec.description,
    url: absoluteUrl(spec.path),
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.name,
        url: absoluteUrl(item.path),
      })),
    },
  });
}

export function breadcrumb(items: readonly BreadcrumbItem[]): Node {
  return withContext({
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  });
}
